import { faCircleCheck, faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Lottie from 'lottie-react'
import { useState, useEffect } from 'react'
import { Transition } from '@headlessui/react'
import { useParams } from 'react-router'

import { type OnboardingPage } from '../routes'
import { ProductEnum } from '../../generated/graphql'
import { useQuotation } from '../context'
import animation from '../../assets/lottie/loading-animation-no-background.json'
import { useOnboardingWorkflow } from '../workflow'
import { track } from '../../utils/analytics'
import { type DealCreateInput, useCreateDealActiveCampaignQuery } from '../../generated/graphql'
const delayRange = [0.5, 1] as const // seconds

const getRandomDelay = () => {
  return Math.random() * (delayRange[1] - delayRange[0]) + delayRange[0]
}

const Main = () => {
  const { id } = useParams()
  const { quotation, updateQuotation } = useQuotation()
  const { proceed } = useOnboardingWorkflow()

  const [steps] = useState(
    (quotation.details.products?.includes(ProductEnum.MultiRisk) &&
      quotation.premium?.multiRisk == null) ||
      (quotation.details.products?.includes(ProductEnum.PublicLiability) &&
        quotation.premium?.publicLiability == null) ||
      (quotation.details.products?.includes(ProductEnum.PublicLiabilityEcomm) &&
        quotation.premium?.publicLiabilityEcomm == null) ||
      (quotation.details.products?.includes(ProductEnum.MreAcheel) &&
        quotation.premium?.mreAcheel == null)
      ? ['Analyse de vos risques', 'Personnalisation de votre contrat', 'Calcul du meilleur tarif']
      : ['Calcul du meilleur tarif']
  )

  const [stepsStart, setStepsStart] = useState<boolean[]>(Array(steps.length).fill(false))
  const [animationFinished, setAnimationFinished] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  let deal = undefined
  let quotPrem
  if (quotation.details.products?.includes(ProductEnum.MreAcheel)) {
    quotPrem = quotation.premium?.mreAcheel
  }

  if (quotPrem !== undefined) {
    deal = {
      quotation: {
        number: quotation.number,
        listName: 'OC - Professionnels Eco',
        pipelineTitle: 'Pro Eco',
        id: quotation.id,
        originalTotal: quotPrem?.originalTotal ? quotPrem.originalTotal : 0,
        discountedTotal: quotPrem?.discountedTotal,
        // applicationFees: quotPrem?.applicationFees?.instalmentFees,
        base: quotPrem?.base,
        status: quotation.status,
        quotation: {
          activeCampaignId: quotation.details.activeCampaignId,
          activeCampaignDealId: quotation.details.activeCampaignDealId,
          contact: {
            email: quotation.details.contact?.email,
            phone: quotation.details.contact?.phone,
            firstName: quotation.details.contact?.firstName,
            lastName: quotation.details.contact?.lastName,
            marketingOptIn: quotation.details.contact?.marketingOptIn,
            userTypeName: quotation.details.contact?.userTypeName,
          },
          products: quotation.details.products,
          turnover: quotation.details.turnover,
          billingAddress: {
            city: quotation.details.billingAddress?.city,
            line1: quotation.details.billingAddress?.line1,
            line2: quotation.details.billingAddress?.line2,
            country: quotation.details.billingAddress?.country,
            postalCode: quotation.details.billingAddress?.postalCode,
          },
          company: {
            name: quotation.details.company?.name,
            nafCode: quotation.details.company?.nafCode,
            siret: quotation.details.company?.siret,
            legalStatus: quotation.details.company?.legalStatus,
            foundedAt: quotation.details.company?.foundedAt,
            address: {
              city: quotation.details.company?.address?.city,
              line1: quotation.details.company?.address?.line1,
              line2: quotation.details.company?.address?.line2,
              postalCode: quotation.details.company?.address?.postalCode,
              country: quotation.details.company?.address?.country,
            },
          },
          mreAcheel: {
            activityId: quotation.details.mreAcheel?.activityId,
            packageOptionMAcheel: quotation.details.mreAcheel?.packageOptionMAcheel,
            surfaceArea: quotation.details.mreAcheel?.surfaceArea,
            // assistanceOption: quotation.details.mreAcheel?.assistanceOption,
            nbSinistres: quotation.details.mreAcheel?.nbSinistres,
            deductibleAmount: quotation.details.mreAcheel?.deductibleAmount,
            extincteursLocaux: quotation.details.mreAcheel?.extincteursLocaux,
            historicalMonument: quotation.details.mreAcheel?.historicalMonument,
            resilieNonPaiement: quotation.details.mreAcheel?.resilieNonPaiement,
            procedureCollective: quotation.details.mreAcheel?.procedureCollective,
            installElectriqueVerif: quotation.details.mreAcheel?.installElectriqueVerif,

            // financialLossAdvantageOption: quotation.details.mreAcheel?.financialLossStandardOption,
            // financialLossCompensationPeriod:
            //   quotation.details.mreAcheel?.financialLossCompensationPeriod,
            // financialLossDeductibleAmount: quotation.details.mreAcheel?.financialLossDeductibleAmount,
            // financialLossStandardOption: quotation.details.mreAcheel?.financialLossStandardOption,
          },
        },
      },
    }
  }

  const shouldCreateDeal =
    quotation.details.products?.includes(ProductEnum.MreAcheel) &&
    quotation.details.mreAcheel?.procedureCollective === false &&
    quotPrem !== undefined &&
    deal !== undefined

  const { data: results, isSuccess } = useCreateDealActiveCampaignQuery(
    { query: deal },
    { enabled: shouldCreateDeal }
  )

  const onProceed = () => {
    if (isProcessing) return
    track({
      event: 'submitted_loading',
      quotation_id: id,
    })

    setAnimationFinished(true)
  }

  useEffect(() => {
    if (!animationFinished || isProcessing) return

    if (!shouldCreateDeal) {
      setIsProcessing(true)
      proceed()
      return
    }

    if (!isSuccess || !results?.createDealActiveCampaign) {
      return
    }

    const activeCampaign = results?.createDealActiveCampaign

    if (!activeCampaign?.activeCampaignId || !activeCampaign?.activeCampaignDealId) {
      proceed()
      return
    }
    const saveQuotation = async () => {
      setIsProcessing(true)

      try {
        await updateQuotation({
          details: {
            activeCampaignId: results?.createDealActiveCampaign?.activeCampaignId,
            activeCampaignDealId: results?.createDealActiveCampaign?.activeCampaignDealId,
          },
        })
        proceed()
      } catch (error) {
        setIsProcessing(false)
      }
    }

    saveQuotation()
  }, [
    animationFinished,
    isProcessing,
    shouldCreateDeal,
    isSuccess,
    results,
    updateQuotation,
    proceed,
  ])

  // Hack to make sure the animation is played
  useEffect(() => {
    setStepsStart(([, ...rest]) => [true, ...rest])
  }, [])

  useEffect(() => {
    track({
      event: 'visited_loading',
      quotation_id: id,
    })
  }, [id])

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <Lottie animationData={animation} loop className="mr-8 h-72 w-72" />
      {steps.map((label, i, array) => (
        <Transition key={`step-${label}`} show={stepsStart[i]} className="text-lg">
          <Transition.Child
            key={label}
            appear
            enter="transition-all transform duration-1000 ease-in-out"
            enterFrom="opacity-0 -translate-y-2"
            enterTo="opacity-100 translate-y-0"
            className="flex flex-row items-center"
          >
            <div className="relative mr-2 h-4 w-4">
              <Transition.Child
                appear
                enter="transition-opacity ease-in-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                className="absolute inset-0 scale-95"
              ></Transition.Child>
              <Transition.Child
                appear
                enter="transition-all ease-in-out transform duration-200"
                enterFrom="opacity-0 scale-30 -translate-x-8"
                enterTo="opacity-100 scale-110 translate-x-0"
                className="absolute inset-0"
                style={{
                  transitionDelay: `${getRandomDelay()}s`,
                }}
                afterEnter={() => {
                  if (i === array.length - 1) {
                    setTimeout(() => onProceed(), getRandomDelay() * 1000)
                  } else {
                    setStepsStart((prev) => prev.map((v, j) => (j === i + 1 ? true : v)))
                  }
                }}
              >
                <FontAwesomeIcon icon={faCircleCheck} className="absolute text-primary-600" />
              </Transition.Child>
            </div>
            <span className="text-left font-medium">{label}</span>
          </Transition.Child>
        </Transition>
      ))}
    </div>
  )
}

const Tips = () => {
  return undefined
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
